import {memo, useCallback, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {isEmptyOrNil, notEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';
import YouTube from 'react-youtube';
import {Show} from '@ultradent/components';
import Icon from '@components/common/Icon';
import {Swiper, SwiperSlide, useSwiper} from 'swiper/react';
import {A11y, Autoplay, EffectFade, FreeMode, Navigation, Pagination, Thumbs} from 'swiper/modules';
import 'swiper/css/effect-fade';

const i18nText = upi.i18n.getTextFromKey;
const HeroVideo = memo( ( {
                              id,
                              slides,
                              isActive,
                              title,
                              titleLogo,
                              subtitle,
                              description,
                              badge,
                              imgUrl,
                              videoId,
                              callToAction,
                              heroClassNames
                          } ) => {

    const swiperInstance = useSwiper();
    const videoPlayer = useRef( null );
    const [isShowingPoster, setIsShowingPoster] = useState( true );
    const [isFullScreen, setIsFullScreen] = useState( false );
    const [hash, setHash] = useState( window.location.hash );
    const lblExpand = i18nText( 'lbl-Expand', 'Expand' );
    const lblCollapse = i18nText( 'lbl-Collapse', 'Collapse' );
    const lblWatchNextVideo = i18nText( 'lbl-WatchNextVideo', 'Watch Next Video' );

    useEffect( () => {
        const onHashChange = () => setHash( window.location.hash );
        window.addEventListener( 'hashchange', onHashChange, false );
        return () => window.removeEventListener( 'hashchange', onHashChange );
    }, [] );

    useEffect( () => {
        swiperInstance.update();
    }, [isFullScreen] );

    useEffect( () => {
        videoPlayer.current?.pauseVideo();
    }, [hash] );

    const handlePlayVideo = useCallback( () => {
        setIsShowingPoster( false );
        swiperInstance?.autoplay?.stop();
        videoPlayer.current?.playVideo();
    }, [videoPlayer.current] );

    const handleExpand = useCallback( () => {
        swiperInstance?.autoplay?.stop();
        setIsFullScreen( !isFullScreen );
    }, [isFullScreen] );

    const handleNextSlide = () => {
        const nextIndex = findNextVideoSlideIndex();
        swiperInstance?.slideTo( nextIndex );
    }

    const handleVideoReady = useCallback( ( event ) => {
        videoPlayer.current = event.target;
    }, [videoPlayer.current] );

    // note -> In "loop" mode this algorithm breaks and starts finding next in reverse order
    //  for now ensure the master Carousel is set to loop false; autoplay still resets to first slide
    const findNextVideoSlideIndex = useCallback( () => {
        const foundIndex = slides.findIndex( item => item.id === id );
        if ( foundIndex === -1 ) {
            return;
        }

        // rearrange and remove currentActive item from queue
        const queue = [
            ...slides.slice( foundIndex ),
            ...slides.slice( 0, foundIndex )
        ];
        queue.splice( 0, 1 );

        // determine the next index with video or fallback to current index
        const nextVideo = queue.find( item => notEmptyOrNil( item.videoId ) );
        return notEmptyOrNil( nextVideo )
            ? slides.findIndex( item => item.id === nextVideo.id )
            : foundIndex;
    }, [id] );

    return (
        <div className="md:flex md:pt-8 items-center bg-white">
            <div className={cx( 'content-video-pane mb-6',
                {'md:w-3/5': !isFullScreen, 'w-full': isFullScreen} )}>
                <div className="relative">
                    <button onClick={handleExpand}
                            className="content-expand-button items-end float-right hidden md:flex rounded-full px-3 py-2 bg-near-white border border-solid border-near-black mb-2">
                        <span>{isFullScreen ? lblCollapse : lblExpand}</span>
                        <Icon.ui name={isFullScreen ? 'left-panel-close' : 'left-panel-open'}
                                 className="block text-md ml-2"/>
                    </button>
                    <div
                        className="video-wrapper rounded-lg overflow-hidden border border-solid border-near-black cursor-pointer">
                        {
                            isShowingPoster ?
                                <figure
                                    className={cx( heroClassNames,
                                        'absolute-center w-full rounded-lg overflow-hidden icon-video-play' )}
                                    onClick={() => setIsShowingPoster( false )}>
                                    <picture>
                                        <source srcSet={`${imgUrl}?w=1520&h=928&q=70&fm=webp`} type="image/webp"/>
                                        <source srcSet={`${imgUrl}?w=1520&h=928`}/>
                                        <img alt={title} src={`${imgUrl}?w=1520&h=928&q=70`}/>
                                    </picture>
                                </figure>
                                :
                                <YouTube
                                    videoId={videoId}
                                    onReady={handleVideoReady}
                                    opts={{
                                        width: '100%',
                                        height: '100%',
                                        playerVars: {
                                            autoplay: 1,
                                            modestbranding: 1,
                                            rel: 0,
                                            controls: 1
                                        }
                                    }}
                                />
                        }
                    </div>
                </div>
            </div>
            <div className={cx( 'md:w-2/5 md:pl-8 lg:pl-12',
                {'hidden': isFullScreen} )}>
                <Show when={badge}>
                    <span className="eyebrow-headline-sm" dangerouslySetInnerHTML={{__html: badge}}/>
                </Show>
                <Show when={titleLogo}>
                    <span dangerouslySetInnerHTML={{__html: titleLogo}}/>
                </Show>
                <h2 className="headline-base mb-4 leading-tight" dangerouslySetInnerHTML={{__html: title}}/>
                <Show when={subtitle}>
                    <p className="subheading-lg uppercase font-normal mb-2"
                       dangerouslySetInnerHTML={{__html: subtitle}}/>
                </Show>
                <p className="text-body" dangerouslySetInnerHTML={{__html: description}}/>
                <div className="flex flex-wrap mt-4">
                    <Show when={notEmptyOrNil( callToAction )}>
                        <button onClick={handlePlayVideo} className="button font-default large primary mb-4 mr-4">
                            <Icon.ui name="video-play" className="block mr-2"/>
                            {callToAction}
                        </button>
                    </Show>
                    <button
                        onClick={handleNextSlide}
                        className="button font-default large tertiary mb-4">{lblWatchNextVideo}</button>
                </div>
            </div>
        </div>
    );
} );

const HeroProduct = ( {
                          brandName,
                          brandDescription,
                          title,
                          subtitle,
                          description,
                          callToAction,
                          callToActionClassName,
                          imgUrl,
                          productUrl,
                          heroClassNames
                      } ) =>
    <div className="md:flex md:py-8 items-center bg-white">
        <div className="mb-6 md:w-3/5">
            <figure className={cx( heroClassNames, 'media-block rounded-lg overflow-hidden' )}>
                <picture className="media-img">
                    <source srcSet={`${imgUrl}?w=1800&q=70&fm=webp`} type="image/webp"/>
                    <source srcSet={`${imgUrl}?w=1800&q=70`}/>
                    <img alt={title} src={`${imgUrl}?w=1800&q=70`}/>
                </picture>
            </figure>
        </div>
        <div className="md:w-2/5 md:pl-8">
            <div className="text-base uppercase mb-0">
                <span dangerouslySetInnerHTML={{__html: brandName}}/> <span
                dangerouslySetInnerHTML={{__html: brandDescription}}/>
            </div>
            <h2 className="headline-base mb-4 leading-tight" dangerouslySetInnerHTML={{__html: title}}/>
            <Show when={subtitle}>
                <p className="subheading-lg uppercase font-normal mb-2" dangerouslySetInnerHTML={{__html: subtitle}}/>
            </Show>
            <p className="text-body" dangerouslySetInnerHTML={{__html: description}}/>
            <div className="flex flex-wrap mt-4">
                <a href={productUrl}
                   target="_blank" rel="noopener"
                   className={cx( 'button font-default large mb-4', callToActionClassName )}>{callToAction ||
                    'Shop Now'}</a>
            </div>
        </div>
    </div>;

const ThumbSlide = ( {
                         contentType,
                         badge,
                         brandName,
                         brandDescription,
                         videoTitle,
                         videoSubtitle,
                         thumbUrl,
                         thumbnailClassNames,
                         thumbnailBackgroundColor
                     } ) => {
    let labelStyle = {};
    if ( thumbnailBackgroundColor ) {
        labelStyle['--slide-label-gradient-color-start'] = `${thumbnailBackgroundColor}B2`; // opaque
        labelStyle['--slide-label-gradient-color-stop'] = `${thumbnailBackgroundColor}00`; // fully transparent
    }

    const slideClassName = cx( thumbnailClassNames,
        'upi-swiper-thumbs-slide-content cursor-pointer relative bg-contain w-full h-0 rounded-lg border border-solid border-near-black img-ratio-8x5 overflow-hidden'
    );

    if ( contentType === 'video' ) {
        return (
            <div
                className={cx( 'slide-thumbnail-video icon-video-play icon-top-right', slideClassName )}
                style={{backgroundImage: `url(${thumbUrl}?w=480&q=70&fm=webp)`}}>
                <div className="slide-label absolute w-full pin-b p-4 pt-8"
                     style={labelStyle}>
                    <span className="text-xs uppercase block">{badge}</span>
                    <p className="text-xs uppercase ellipsis">{videoSubtitle}</p>
                    <h4 className="subheading-base leading-tight mb-0" dangerouslySetInnerHTML={{__html: videoTitle}}/>
                </div>
            </div>
        );
    }

    return (
        <div className={cx( 'slide-thumbnail-product', slideClassName )}
             style={{backgroundImage: `url(${thumbUrl}?w=480&q=70&fm=webp)`}}>
                <div className="slide-label absolute w-full pin-b p-4 pt-8">
                    <span className="text-xs uppercase block">{badge}</span>
                    <h4 className="subheading-base leading-tight mb-0" dangerouslySetInnerHTML={{__html: brandName}}/>
                    <p className="text-xs uppercase ellipsis">{brandDescription}</p>
                </div>
        </div>
    )
}

function useHashNavigation () {
    // note -> this is a custom hook to manage hash navigation
    // it allows us to set and get the hash value without adding to the browser history stack while still
    // triggering hashchange events for listeners
    return {
        set: ( hash ) => {
            const nextUrl = hash ? `#${hash}` : window.location.pathname + window.location.search
            history.replaceState( null, '', nextUrl );
            window.dispatchEvent( new HashChangeEvent( 'hashchange' ) );
        },
        get: () => window.location.hash.replace( '#', '' )
    }

}

const SwiperMasterDetail = ( {slides} ) => {
    const windowHash = useHashNavigation();
    const startAtIndex = slides.findIndex( item => item.id === windowHash.get() );

    const [autoplaySetting, setAutoplaySetting] = useState( isEmptyOrNil( windowHash.get() ) ? {
        delay: 4200,
        disableOnInteraction: true
    } : false );
    const [controlSwiper, setControlSwiper] = useState( null );
    const [thumbsSwiper, setThumbsSwiper] = useState( null );

    useEffect( () => {
        document.documentElement.classList.add( 'smooth-scrolling' );
    }, [] );

    function handleInteraction ( e ) {
        controlSwiper?.autoplay?.stop();
    }

    return (
        <div onClick={handleInteraction} onTouchStart={handleInteraction}>
            <Swiper
                onSwiper={setControlSwiper}
                initialSlide={startAtIndex}
                autoHeight={true}
                loop={true}
                autoplay={autoplaySetting}
                thumbs={{swiper: thumbsSwiper}}
                effect="fade"
                className="upi-swiper-master"
                onSlideChange={( swiper, e ) => {
                    const hash = swiper.slides[swiper.activeIndex].getAttribute( 'data-hash' );
                    windowHash.set( hash );
                }}
                modules={[A11y, Autoplay, Thumbs, FreeMode, EffectFade]}
            >
                {slides?.map( slide =>
                    <SwiperSlide data-hash={slide.id} key={slide.id}>
                        {sSlide =>
                            slide.contentType === 'video'
                                ? <HeroVideo isActive={sSlide.isActive} slides={slides} {...slide} />
                                : <HeroProduct {...slide} />
                        }
                    </SwiperSlide>
                )}
            </Swiper>
            <Swiper
                onSwiper={setThumbsSwiper}
                initialSlide={startAtIndex}
                loop={true}
                spaceBetween={26}
                slidesPerView={1}
                navigation
                pagination={{clickable: true}}
                watchSlidesProgress={true}
                className="upi-swiper-thumbs"
                breakpoints={{
                    768: {
                        slidesPerView: 2
                    },
                    1024: {
                        slidesPerView: 3
                    },
                    1140: {
                        slidesPerView: 4
                    }
                }}
                modules={[Navigation, Pagination, A11y, Thumbs, FreeMode]}
            >
                {slides?.map( ( slide ) =>
                    <SwiperSlide key={slide.id}>
                        <ThumbSlide {...slide}/>
                    </SwiperSlide>
                )}
            </Swiper>
        </div>
    );
};

const CarouselContentViewer = ( {data} ) => {
    return (
        <SwiperMasterDetail slides={data.content}/>
    );
};

export default CarouselContentViewer;